/*
 * Purpose: Immutable mission dossier viewer — full AI audit trail (read-only)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { useMemo } from 'react';
import { FileText, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useMissionDossierStore } from '@/stores/missionDossierStore';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { MISSION_DOSSIER_DROPBOX_NOTICE } from '@/services/mission-dossier/types';
import { cn } from '@/lib/utils';

interface MissionDossierPanelProps {
  className?: string;
}

export function MissionDossierPanel({ className }: MissionDossierPanelProps) {
  const missionSessionId = useMissionKnowledgeStore((s) => s.missionSessionId);
  const entries = useMissionDossierStore((s) => s.entries);
  const lastSyncError = useMissionDossierStore((s) => s.lastSyncError);

  const exportJson = useMemo(
    () =>
      JSON.stringify(
        {
          missionSessionId,
          dropboxNotice: MISSION_DOSSIER_DROPBOX_NOTICE,
          entries,
        },
        null,
        2
      ),
    [missionSessionId, entries]
  );

  function downloadDossier() {
    const blob = new Blob([exportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${missionSessionId || 'mission'}-dossier.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div
      id="mission-dossier"
      className={cn(
        'rounded-md border border-border/60 bg-background/80 p-3 space-y-3',
        className
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:justify-between">
        <div className="flex items-start gap-2 min-w-0">
          <FileText className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground flex flex-wrap items-center gap-2">
              Mission dossier
              <Badge variant="outline" className="text-[10px] font-normal">
                <Lock className="h-3 w-3 mr-1" aria-hidden />
                Append-only
              </Badge>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              Everything the AI thinks and does is recorded here. The AI reads this file as a{' '}
              <strong>read-only dropbox</strong> — nothing silent, nothing rewritten.
            </p>
            {lastSyncError && (
              <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1">{lastSyncError}</p>
            )}
          </div>
        </div>
        {entries.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="shrink-0 h-8 text-xs"
            onClick={downloadDossier}
          >
            Download JSON
          </Button>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic">
          No entries yet — interpret or plan to start the immutable log.
        </p>
      ) : (
        <ol className="max-h-64 overflow-y-auto space-y-2 pr-1 text-[11px]">
          {[...entries].reverse().map((e) => (
            <li
              key={e.id}
              className="rounded border border-border/50 bg-muted/20 px-2.5 py-2 font-mono leading-relaxed"
            >
              <div className="flex flex-wrap gap-1.5 mb-1 font-sans">
                <Badge variant="secondary" className="text-[9px] h-5">
                  #{e.seq}
                </Badge>
                <Badge variant="outline" className="text-[9px] h-5">
                  {e.actor}
                </Badge>
                <Badge variant="outline" className="text-[9px] h-5">
                  {e.kind}
                </Badge>
                {!e.syncedAt && (
                  <Badge variant="outline" className="text-[9px] h-5">
                    local
                  </Badge>
                )}
              </div>
              <p className="text-foreground font-sans font-medium">{e.summary}</p>
              {e.reasoning && (
                <p className="text-muted-foreground mt-1">
                  <span className="text-foreground/70">Reasoning:</span> {e.reasoning}
                </p>
              )}
              {e.action && (
                <p className="text-muted-foreground mt-0.5">
                  <span className="text-foreground/70">Action:</span> {e.action}
                </p>
              )}
              <p className="text-[10px] text-muted-foreground/80 mt-1">{e.at}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
